// screen: Team — Users and invitations
// Also: Team — Notification rules (second tab). Studio spec §4.14; control-plane change notifications: /v1/users, /v1/invitations, /v1/notification-rules. The server enforces every guard (owner role, last owner,
// self-disable); the UI hides what the caller cannot do and shows the API's reason when it refuses.
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { BellRing, MailPlus, RotateCcw, Send, Trash2, UserX, UserCheck, Users } from 'lucide-react';
import type { ApiError } from '@viewstream/api-client';
import { fmtDateTime } from '@viewstream/i18n';
import {
  Badge, Button, Checkbox, ConfirmByNameDialog, EmptyState, ErrorState, Field, Input, Select, Skeleton, Switch,
  Tabs, TabsContent, TabsList, TabsTrigger, useToast,
} from '@viewstream/ui';
import { appTitle } from '~/app/brand';
import { useTenant } from '~/app/tenant';
import { useLocale } from '~/app/locale';
import { can, isInterhostRole } from '~/app/permissions';
import {
  ROLES, problemText, useCreateRule, useDeleteRule, useInvitations, useInvite, usePatchRule, usePatchUser, useRemoveUser,
  useResendInvitation, useRevokeInvitation, useRules, useTestRule, useUsers,
  type NotificationRule, type NotificationRuleInput, type PendingInvitation, type Role, type TeamMember,
} from '~/app/team';

export const Route = createFileRoute('/_layout/t/$slug/team')({ component: Screen });

function useRoleLabels(): Record<Role, string> {
  const { t } = useLingui();
  return { viewer: t`Viewer`, editor: t`Editor`, publisher: t`Publisher`, engineer: t`Engineer`, admin: t`Admin`, owner: t`Owner` };
}

function Screen() {
  const { t } = useLingui();
  const { tenant, scopes } = useTenant();
  React.useEffect(() => { document.title = appTitle(t`Team`); }, [t]);
  const team = can(scopes, 'team:manage');
  const notif = can(scopes, 'notifications:manage');
  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t`Team`}</h1>
      <Tabs defaultValue={team ? 'users' : 'notifications'}>
        <TabsList>
          {team ? <TabsTrigger value="users">{t`Users and invitations`}</TabsTrigger> : null}
          {notif ? <TabsTrigger value="notifications">{t`Notification rules`}</TabsTrigger> : null}
        </TabsList>
        {team ? <TabsContent value="users" className="space-y-8 pt-4"><UsersTab tenant={tenant.slug} /></TabsContent> : null}
        {notif ? <TabsContent value="notifications" className="pt-4"><RulesTab tenant={tenant.slug} /></TabsContent> : null}
      </Tabs>
    </div>
  );
}

// ---------------------------------------------------------------- users

function UsersTab({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const q = useUsers(tenant);
  return (
    <>
      <section aria-labelledby="invite-h" className="space-y-3">
        <h2 id="invite-h" className="text-lg font-semibold">{t`Invite a user`}</h2>
        <InviteForm tenant={tenant} />
      </section>
      <section aria-labelledby="users-h" className="space-y-3">
        <h2 id="users-h" className="text-lg font-semibold">{t`Users`}</h2>
        {q.isError ? <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load users`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />
          : q.isPending ? <Skeleton className="h-32" /> : <UsersTable tenant={tenant} members={q.data} />}
      </section>
      <section aria-labelledby="pending-h" className="space-y-3">
        <h2 id="pending-h" className="text-lg font-semibold">{t`Pending invitations`}</h2>
        <PendingInvitations tenant={tenant} />
      </section>
    </>
  );
}

function assignableRoles(callerRole: string): Role[] {
  return callerRole === 'owner' || isInterhostRole(callerRole) ? ROLES : ROLES.filter((r) => r !== 'owner');
}

function InviteForm({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const { tenant: me } = useTenant();
  const labels = useRoleLabels();
  const invite = useInvite(tenant);
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState<Role>('editor');
  const [err, setErr] = React.useState<string | undefined>();
  const [sent, setSent] = React.useState<string | undefined>();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    invite.mutate({ email: email.trim(), role }, {
      onSuccess: () => { setSent(email.trim()); setEmail(''); },
      onError: (x) => setErr(problemText(x)),
    });
  };
  return (
    <div className="space-y-2 rounded-lg border border-border p-3">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Field id="invite-email" label={t`Email`} error={err}>
            <Input type="email" required dir="ltr" autoComplete="off" placeholder="name@example.co.il" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
        </div>
        <div className="mb-4">
          <span aria-hidden className="mb-1 block text-sm font-medium">{t`Role`}</span>
          <Select ariaLabel={t`Role`} value={role} onValueChange={(v) => setRole(v as Role)} options={assignableRoles(me.role).map((r) => ({ value: r, label: labels[r] }))} />
        </div>
        <Button type="submit" className="mb-4" loading={invite.isPending} disabled={!email.trim()}>
          <MailPlus className="size-4" aria-hidden /> {t`Send invitation`}
        </Button>
      </form>
      {sent ? (
        <div role="status" className="rounded border border-ok/30 bg-ok/10 p-2 text-sm" data-testid="invite-sent">
          {/* security audit 2026-10-06 H1: the link goes only to the invited address; resend if it did not arrive */}
          <p>{t`Invitation e-mailed to ${sent}. The link in the e-mail is valid for 7 days; if it does not arrive, resend it from the pending invitations.`}</p>
          <div className="mt-1 flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSent(undefined)}>{t`Done`}</Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function UsersTable({ tenant, members }: { tenant: string; members: TeamMember[] }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const { tenant: me } = useTenant();
  const labels = useRoleLabels();
  const toast = useToast();
  const patch = usePatchUser(tenant);
  const remove = useRemoveUser(tenant);
  const [removing, setRemoving] = React.useState<TeamMember | undefined>();
  const ownerish = me.role === 'owner' || isInterhostRole(me.role);
  const fail = (x: unknown) => toast.push({ title: t`Not changed`, description: problemText(x), tone: 'danger' });
  const at = (iso: string | null | undefined) => (iso ? fmtDateTime(iso, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' }) : t`Never`);
  if (members.length === 0) return <EmptyState icon={<Users className="size-8" aria-hidden />} title={t`No users yet`} />;
  return (
    <>
      <table className="w-full text-sm" aria-label={t`Users`}>
        <thead className="text-xs text-fg-muted">
          <tr><th className="text-start">{t`User`}</th><th className="text-start">{t`Role`}</th><th className="text-start">{t`Status`}</th><th className="text-start">{t`Last sign-in`}</th><th><span className="sr-only">{t`Actions`}</span></th></tr>
        </thead>
        <tbody>
          {members.map((m) => {
            const locked = (m.role === 'owner' && !ownerish);
            return (
              <tr key={m.id} className="border-t border-border align-middle" data-testid={`user-${m.email}`}>
                <td className="py-2">
                  <span className="block">{m.name || m.email} {m.you ? <Badge>{t`You`}</Badge> : null}</span>
                  <span className="text-xs text-fg-muted ltr-isolate">{m.email}</span>
                </td>
                <td>
                  {locked ? labels[m.role] : (
                    <Select ariaLabel={t`Role of ${m.email}`} className="min-w-32" value={m.role}
                      options={assignableRoles(me.role).concat(m.role === 'owner' && !ownerish ? ['owner'] : []).map((r) => ({ value: r, label: labels[r] }))}
                      onValueChange={(v) => patch.mutate({ id: m.id, role: v as Role }, { onSuccess: () => toast.push({ title: t`Role changed`, tone: 'ok' }), onError: fail })} />
                  )}
                </td>
                <td>{m.status === 'active' ? <Badge tone="ok">{t`Active`}</Badge> : <Badge tone="warn">{t`Disabled`}</Badge>}</td>
                <td>{at(m.last_login_at)}</td>
                <td className="whitespace-nowrap text-end">
                  {m.you || locked ? null : m.status === 'active' ? (
                    <Button variant="ghost" size="sm" onClick={() => patch.mutate({ id: m.id, status: 'disabled' }, { onError: fail })}>
                      <UserX className="size-4" aria-hidden /> {t`Disable`}
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => patch.mutate({ id: m.id, status: 'active' }, { onError: fail })}>
                      <UserCheck className="size-4" aria-hidden /> {t`Enable`}
                    </Button>
                  )}
                  {locked ? null : (
                    <Button variant="ghost" size="icon" aria-label={t`Remove ${m.email}`} onClick={() => setRemoving(m)}>
                      <Trash2 className="size-4" aria-hidden />
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {removing ? (
        <ConfirmByNameDialog open onOpenChange={(o) => { if (!o) setRemoving(undefined); }} name={removing.email}
          title={t`Remove user from the tenant`} description={t`They lose access to this tenant immediately; their account and other tenants stay. Type the email to confirm.`}
          labels={{ field: t`Email`, confirm: t`Remove`, cancel: t`Cancel`, close: t`Close` }} busy={remove.isPending}
          onConfirm={() => remove.mutateAsync(removing.id).then(() => setRemoving(undefined), (x) => { setRemoving(undefined); fail(x); })} />
      ) : null}
    </>
  );
}

function PendingInvitations({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const { locale, tz } = useLocale();
  const labels = useRoleLabels();
  const toast = useToast();
  const q = useInvitations(tenant);
  const resend = useResendInvitation(tenant);
  const revoke = useRevokeInvitation(tenant);
  const fail = (x: unknown) => toast.push({ title: t`Not changed`, description: problemText(x), tone: 'danger' });
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load invitations`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (q.isPending) return <Skeleton className="h-16" />;
  if (q.data.length === 0) return <p className="text-sm text-fg-muted">{t`No pending invitations.`}</p>;
  return (
    <table className="w-full text-sm" aria-label={t`Pending invitations`}>
      <thead className="text-xs text-fg-muted">
        <tr><th className="text-start">{t`Email`}</th><th className="text-start">{t`Role`}</th><th className="text-start">{t`Expires`}</th><th><span className="sr-only">{t`Actions`}</span></th></tr>
      </thead>
      <tbody>
        {q.data.map((i: PendingInvitation) => (
          <tr key={i.id} className="border-t border-border" data-testid={`invitation-${i.email}`}>
            <td className="py-2"><span className="ltr-isolate">{i.email}</span></td>
            <td>{labels[i.role as Role] ?? i.role}</td>
            <td>{fmtDateTime(i.expires_at, { locale, tz }, { dateStyle: 'short', timeStyle: 'short' })}</td>
            <td className="whitespace-nowrap text-end">
              <Button variant="ghost" size="sm" disabled={resend.isPending}
                onClick={() => resend.mutate(i.id, { onSuccess: () => toast.push({ title: t`Invitation sent again with a new link`, tone: 'ok' }), onError: fail })}>
                <RotateCcw className="size-4" aria-hidden /> {t`Resend`}
              </Button>
              <Button variant="ghost" size="sm" disabled={revoke.isPending}
                onClick={() => revoke.mutate(i.id, { onSuccess: () => toast.push({ title: t`Invitation revoked`, tone: 'ok' }), onError: fail })}>
                <Trash2 className="size-4" aria-hidden /> {t`Revoke`}
              </Button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------- notification rules

function useEventLabels(): Record<string, string> {
  const { t } = useLingui();
  return {
    'feed.lost': t`Feed lost`, 'feed.restored': t`Feed restored`, 'recording.redundancy': t`Recording redundancy (two recorders)`, 'asset.failed': t`Encoding failed`, 'asset.ready': t`Asset ready`,
    'storage.warning': t`Storage quota 80 % / 95 %`, 'fast.check_failed': t`FAST schedule blocked by the playlist checker`, '*': t`All events`,
  };
}

function RulesTab({ tenant }: { tenant: string }) {
  const { t } = useLingui();
  const q = useRules(tenant);
  if (q.isError) return <ErrorState error={q.error as ApiError} onRetry={() => q.refetch()} labels={{ title: t`Could not load rules`, retry: t`Retry`, requestId: t`Request id`, copy: t`Copy` }} />;
  if (q.isPending) return <Skeleton className="h-32" />;
  return (
    <div className="space-y-6">
      <p className="text-sm text-fg-muted">{t`Every event also appears in the bell in the header for everyone in the tenant. Rules send it on by email or Telegram.`}</p>
      <RuleForm tenant={tenant} events={q.data.events} telegram={q.data.telegram} />
      {q.data.data.length === 0 ? <EmptyState icon={<BellRing className="size-8" aria-hidden />} title={t`No notification rules yet`} />
        : <ul className="divide-y divide-border rounded-lg border border-border" aria-label={t`Notification rules`}>{q.data.data.map((r) => <RuleRow key={r.id} tenant={tenant} rule={r} />)}</ul>}
    </div>
  );
}

function RuleForm({ tenant, events, telegram }: { tenant: string; events: string[]; telegram: boolean }) {
  const { t } = useLingui();
  const ev = useEventLabels();
  const members = useUsers(tenant);
  const create = useCreateRule(tenant);
  const [name, setName] = React.useState('');
  const [picked, setPicked] = React.useState<string[]>(['feed.lost', 'feed.restored']);
  const [channel, setChannel] = React.useState<'email' | 'telegram'>('email');
  const [emails, setEmails] = React.useState<string[]>([]);
  const [chats, setChats] = React.useState('');
  const [err, setErr] = React.useState<string | undefined>();
  const later = [t`Certificate expiring`, t`Traffic above commit`, t`Viewers above N`];
  const recipients = channel === 'email' ? emails : chats.split(/[\s,]+/).filter(Boolean);
  const toggle = (list: string[], set: (v: string[]) => void, v: string, on: boolean) => set(on ? [...list, v] : list.filter((x) => x !== v));
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErr(undefined);
    create.mutate({ name: name.trim(), events: picked as NotificationRuleInput['events'], channel, recipients, enabled: true }, {
      onSuccess: () => { setName(''); setEmails([]); setChats(''); },
      onError: (x) => setErr(problemText(x)),
    });
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-border p-3" aria-labelledby="rule-h">
      <h2 id="rule-h" className="font-semibold">{t`New rule`}</h2>
      <Field id="rule-name" label={t`Rule name`} error={err}>
        <Input required maxLength={100} placeholder={t`e.g. NOC on-call`} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">{t`When`}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {events.map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <Checkbox checked={picked.includes(k)} onCheckedChange={(v) => toggle(picked, setPicked, k, v === true)} /> {ev[k] ?? k}
            </label>
          ))}
          {later.map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm text-fg-muted">
              <Checkbox disabled checked={false} /> {l} <Badge>{t`later`}</Badge>
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend className="mb-1 text-sm font-medium">{t`Send by`}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1" role="radiogroup">
          <label className="flex items-center gap-2 text-sm"><input type="radio" name="channel" checked={channel === 'email'} onChange={() => setChannel('email')} /> {t`Email`}</label>
          <label className={`flex items-center gap-2 text-sm ${telegram ? '' : 'text-fg-muted'}`}>
            <input type="radio" name="channel" disabled={!telegram} checked={channel === 'telegram'} onChange={() => setChannel('telegram')} /> {t`Telegram`}
            {telegram ? null : <Badge>{t`not configured`}</Badge>}
          </label>
          {[t`SMS`, t`Slack`, t`Webhook`].map((l) => (
            <label key={l} className="flex items-center gap-2 text-sm text-fg-muted"><input type="radio" name="channel" disabled /> {l} <Badge>{t`later`}</Badge></label>
          ))}
        </div>
      </fieldset>
      {channel === 'email' ? (
        <fieldset>
          <legend className="mb-1 text-sm font-medium">{t`Recipients (members of this tenant)`}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {(members.data ?? []).filter((m) => m.status === 'active').map((m) => (
              <label key={m.id} className="flex items-center gap-2 text-sm">
                <Checkbox checked={emails.includes(m.email)} onCheckedChange={(v) => toggle(emails, setEmails, m.email, v === true)} />
                <span className="ltr-isolate">{m.email}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : (
        <Field id="rule-chats" label={t`Telegram chat ids`} hint={t`Numeric ids, comma separated (groups start with -). Add the ViewStream bot to the group first.`}>
          <Input dir="ltr" inputMode="numeric" placeholder="-1001234567890" value={chats} onChange={(e) => setChats(e.target.value)} />
        </Field>
      )}
      <Button type="submit" loading={create.isPending} disabled={!name.trim() || picked.length === 0 || recipients.length === 0}>{t`Add rule`}</Button>
    </form>
  );
}

function RuleRow({ tenant, rule }: { tenant: string; rule: NotificationRule }) {
  const { t } = useLingui();
  const ev = useEventLabels();
  const toast = useToast();
  const patch = usePatchRule(tenant);
  const del = useDeleteRule(tenant);
  const test = useTestRule(tenant);
  const [confirm, setConfirm] = React.useState(false);
  const fail = (x: unknown) => toast.push({ title: t`Failed`, description: problemText(x), tone: 'danger' });
  return (
    <li className="flex flex-wrap items-center gap-3 p-3" data-testid={`rule-${rule.name}`}>
      <div className="min-w-0 flex-1">
        <span className="block font-medium">{rule.name}</span>
        <span className="text-xs text-fg-muted">
          {rule.events.map((k) => ev[k] ?? k).join(', ')} → {rule.channel === 'email' ? t`Email` : t`Telegram`} · <span className="ltr-isolate">{rule.recipients.join(', ')}</span>
        </span>
      </div>
      <Switch checked={rule.enabled} aria-label={t`Enabled`} onCheckedChange={(v) => patch.mutate({ id: rule.id, enabled: v }, { onError: fail })} />
      <Button variant="secondary" size="sm" disabled={test.isPending}
        onClick={() => test.mutate(rule.id, {
          onSuccess: (sends) => {
            const bad = sends.filter((s) => s.status !== 'sent');
            toast.push(bad.length ? { title: t`Test failed for ${bad.length} recipient(s)`, description: bad.map((s) => `${s.recipient}: ${s.error ?? ''}`).join('; '), tone: 'danger' }
              : { title: t`Test notification sent`, tone: 'ok' });
          },
          onError: fail,
        })}>
        <Send className="size-4" aria-hidden /> {t`Send test`}
      </Button>
      <Button variant="ghost" size="icon" aria-label={t`Delete rule ${rule.name}`} onClick={() => setConfirm(true)}><Trash2 className="size-4" aria-hidden /></Button>
      {confirm ? (
        <ConfirmByNameDialog open onOpenChange={setConfirm} name={rule.name}
          title={t`Delete notification rule`} description={t`Type the rule name to confirm.`}
          labels={{ field: t`Rule name`, confirm: t`Delete`, cancel: t`Cancel`, close: t`Close` }} busy={del.isPending}
          onConfirm={() => del.mutateAsync(rule.id).then(() => setConfirm(false))} />
      ) : null}
    </li>
  );
}
